import { Accordion, AccordionDetails, AccordionSummary, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { useEffect, useRef } from "react";

import { useBoolean } from "src/hooks/use-boolean";
import Iconify from "src/components/iconify";
import VideoPlayer from "src/components/videoplayer/videoplayer";

type Section = { key: string; title: string; content: string; video: string };
type HelpDialogProps = { section?: string; request?: number; hideTrigger?: boolean; open?: boolean; onClose?: () => void; onOpen?: () => void };

// FS-2026-063: embedded in the new dialog as well as the original standalone help.
export function AdminHelpContent({ section }: HelpDialogProps) {
  const theme = useTheme();
  // Define your sections
  const sections: Section[] = [
    {
      key: "admin_help_section_admin_start_steps",
      title: "admin_help_section_admin_start_steps",
      content: "admin_help_section_admin_start_steps_content",
      video: "", //"/assets/videos/about/foggo_promo_1_2022.mp4",
    },
    {
      key: "admin_help_section_services",
      title: "admin_help_section_services",
      content: "admin_help_section_services_content",
      video: "",
    },
    {
      key: "admin_help_section_profile",
      title: "admin_help_section_profile",
      content: "admin_help_section_profile_content",
      video: "",
    },
    {
      key: "admin_help_section_images",
      title: "admin_help_section_images",
      content: "admin_help_section_images_content",
      video: "",
    },
    {
      key: "admin_help_section_sports",
      title: "admin_help_section_sports",
      content: "admin_help_section_sports_content",
      video: "",
    },
    {
      key: "admin_help_section_venues",
      title: "admin_help_section_venues",
      content: "admin_help_section_venues_content",
      video: "",
    },
    {
      key: "admin_help_section_billing_infos",
      title: "admin_help_section_billing_infos",
      content: "admin_help_section_billing_infos_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_global",
      title: "admin_help_section_settings_global",
      content: "admin_help_section_settings_global_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_pricing",
      title: "admin_help_section_settings_pricing",
      content: "admin_help_section_settings_pricing_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_reservations",
      title: "admin_help_section_settings_reservations",
      content: "admin_help_section_settings_reservations_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_trainings",
      title: "admin_help_section_settings_trainings",
      content: "admin_help_section_settings_trainings_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_blockings",
      title: "admin_help_section_settings_blockings",
      content: "admin_help_section_settings_blockings_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_passes",
      title: "admin_help_section_settings_passes",
      content: "admin_help_section_settings_passes_content",
      video: "",
    },
    {
      key: "admin_help_section_settings_tickets",
      title: "admin_help_section_settings_tickets",
      content: "admin_help_section_settings_tickets_content",
      video: "",
    },
    {
      key: "admin_help_section_calendar",
      title: "admin_help_section_calendar",
      content: "admin_help_section_calendar_content",
      video: "",
    },
    {
      key: "admin_help_section_transactions",
      title: "admin_help_section_transactions",
      content: "admin_help_section_transactions_content",
      video: "",
    },
    {
      key: "admin_help_section_players",
      title: "admin_help_section_players",
      content: "admin_help_section_players_content",
      video: "",
    },
    {
      key: "admin_help_section_analytics",
      title: "admin_help_section_analytics",
      content: "admin_help_section_analytics_content",
      video: "",
    },
  ];

  // If a section prop is provided, reorder so that it appears first.
  const orderedSections = section
    ? [
        ...sections.filter((sec) => sec.key === section),
        ...sections.filter((sec) => sec.key !== section),
      ]
    : sections;

  return <Stack spacing={2} sx={{ py: 2 }}>
            {orderedSections.map((sec) => (
              <Accordion
                key={sec.key}
                defaultExpanded={sec.key === section}
                sx={{
                  // When expanded, the root element of Accordion gets the "Mui-expanded" class.
                  // Here we override its background color to green.
                  "&.Mui-expanded": {
                    backgroundColor: theme.palette.primary.main,
                    color: "primary.contrastText",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Iconify
                      icon="ph:caret-down-bold"
                      width={20}
                      sx={{ float: "left" }}
                    />
                  }
                >
                  <Typography variant="subtitle1">{t(sec.title)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction={"column"} spacing={2}>
                    <Stack>
                      {/* <Typography>{t(sec.content)}</Typography> */}
                      <Trans i18nKey={sec.content}></Trans>
                    </Stack>
                    {sec.video && (
                      <Stack alignItems={"center"}>
                        <VideoPlayer src={t(sec.video)} alt={t(sec.title)} />
                      </Stack>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
  </Stack>;
}

export default function AdminHelpDialog({ section, request = 0, hideTrigger = false, open, onClose, onOpen }: HelpDialogProps) {
  const dialog = useBoolean();
  const previousRequest = useRef(request);
  useEffect(() => {
    if (request !== previousRequest.current) {
      previousRequest.current = request;
      dialog.onTrue();
    }
  }, [request, dialog.onTrue]);
  const close = onClose ?? dialog.onFalse;
  return <>
    {!hideTrigger && <Tooltip title={t("help")}>
      <IconButton aria-label={t("help")} onClick={onOpen ?? dialog.onTrue} sx={{ pt: 0.5, color: "info.main" }}>
        <Iconify icon="mingcute:question-line" />
      </IconButton>
    </Tooltip>}
    <Dialog fullScreen PaperProps={{ sx: { maxWidth: 1000 } }} open={open ?? dialog.value} onClose={close} aria-labelledby="admin-help-title">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography id="admin-help-title" variant="h6">{t("help")}</Typography>
          <IconButton aria-label={t("close")} onClick={close}><Iconify icon="mingcute:close-line" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent><AdminHelpContent section={section} /></DialogContent>
    </Dialog>
  </>;
}
